// Updatesupplier.tsx
import React, { FC, useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { useAuth } from '../../../app/modules/auth';
import { KTSVG } from '../../../_metronic/helpers';

const API_URL = process.env.REACT_APP_API_URL;

interface UpdatesupplierProps {
  show: boolean;
  handleClose: () => void;
  suppliertoUpdate: any;
  allsuppliersrefetch: () => void;
  summaryrefetch: () => void;
  outstandingrefetch: () => void;
}

const Updatesupplier: FC<UpdatesupplierProps> = ({
  show,
  handleClose,
  suppliertoUpdate,
  allsuppliersrefetch,
  summaryrefetch,
  outstandingrefetch,
}) => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    supplier_id: suppliertoUpdate.supplier_id || 0,
    supplier_firstname: suppliertoUpdate.supplier_firstname || '',
    supplier_secondname: suppliertoUpdate.supplier_secondname || '',
    supplier_phone_number: suppliertoUpdate.supplier_phone_number || '',
    supplier_email: suppliertoUpdate.supplier_email || '',
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setFormData({
      supplier_id: suppliertoUpdate.supplier_id || 0,
      supplier_firstname: suppliertoUpdate.supplier_firstname || '',
      supplier_secondname: suppliertoUpdate.supplier_secondname || '',
      supplier_phone_number: suppliertoUpdate.supplier_phone_number || '',
      supplier_email: suppliertoUpdate.supplier_email || '',
    });
    setError(null);
  }, [suppliertoUpdate]);

  const updateSupplierMutation = useMutation(
    async (updatedSupplier: any) => {
      const response = await axios.put(`${API_URL}/suppliers/${updatedSupplier.supplier_id}`, {
        ...updatedSupplier,
        user_id: currentUser?.user_id,
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('allsuppliers');
        allsuppliersrefetch();
        summaryrefetch();
        outstandingrefetch();
        setError(null);
        handleClose();
      },
      onError: (error: any) => {
        setError(error.response?.data?.message || 'Failed to update supplier');
      },
    }
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.supplier_firstname || !formData.supplier_phone_number) {
      setError('First name and phone number are required');
      return;
    }
    updateSupplierMutation.mutate(formData);
  };

  return (
    <Modal
      className='modal fade'
      id='kt_modal_update_supplier'
      data-backdrop='static'
      tabIndex={-1}
      role='dialog'
      show={show}
      centered
      dialogClassName='modal-lg'
      aria-hidden='true'
      onHide={handleClose}
    >
      <div className='modal-content'>
        <div className='modal-header'>
          <h3 className='fw-bolder'>Update Supplier
          <span className='h-10px border-gray-200 border-start mx-2'></span>
          <span className='text-muted fw-bold fs-6'>{suppliertoUpdate.supplier_id}</span></h3>

          <div
            className='btn btn-icon btn-sm btn-active-light-primary ms-2'
            onClick={handleClose}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2x' />
          </div>
        </div>
        <div className='modal-body'>
          <form className='form' noValidate onSubmit={handleSubmit}>
            {/* begin::Input group */}
            <div className='row'>
              <div className='col-lg-6 fv-row mb-7'>
                {/* begin::Label */}
                <label className='required fw-bold fs-6 mb-2'>First Name</label>
                {/* end::Label */}
                <input
                  type='text'
                  name='supplier_firstname'
                  placeholder='Enter first name'
                  className='form-control form-control-lg form-control-solid'
                  autoComplete='off'
                  value={formData.supplier_firstname}
                  onChange={handleChange}
                />
              </div>

              <div className='col-lg-6 fv-row mb-7'>
                {/* begin::Label */}
                <label className='fw-bold fs-6 mb-2'>Second Name</label>
                {/* end::Label */}
                <input
                  type='text'
                  name='supplier_secondname'
                  placeholder='Enter second name'
                  className='form-control form-control-lg form-control-solid'
                  autoComplete='off'
                  value={formData.supplier_secondname}
                  onChange={handleChange}
                />
              </div>
            </div>
            {/* end::Input group */}

            {/* begin::Input group */}
            <div className='row'>
              <div className='col-lg-6 fv-row mb-7'>
                {/* begin::Label */}
                <label className='required fw-bold fs-6 mb-2'>Phone Number</label>
                {/* end::Label */}
                <input
                  type='text'
                  name='supplier_phone_number'
                  placeholder='Enter phone number'
                  className='form-control form-control-lg form-control-solid'
                  autoComplete='off'
                  value={formData.supplier_phone_number}
                  onChange={handleChange}
                />
              </div>

              <div className='col-lg-6 fv-row mb-7'>
                {/* begin::Label */}
                <label className='fw-bold fs-6 mb-2'>Email</label>
                {/* end::Label */}
                <input
                  type='email'
                  name='supplier_email'
                  placeholder='Enter email'
                  className='form-control form-control-lg form-control-solid'
                  autoComplete='off'
                  value={formData.supplier_email}
                  onChange={handleChange}
                />
              </div>
            </div>
            {/* end::Input group */}

            {/* Error display */}
            {error && (
              <div className='fv-row mb-7'>
                <div className='alert alert-danger d-flex align-items-center p-5'>
                  <KTSVG
                    path='/media/icons/duotune/general/gen044.svg'
                    className='svg-icon-2hx svg-icon-danger me-4'
                  />
                  <div className='d-flex flex-column'>
                    <span className='fw-bold fs-6'>{error}</span>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>

        <div className='modal-footer border-0 mt-n5'>
          <button type='button' className='btn btn-light-primary' onClick={handleClose}>
            Cancel
          </button>
          <button
            id='submit'
            type='button'
            className='btn btn-info'
            onClick={handleSubmit}
            disabled={updateSupplierMutation.isLoading || !formData.supplier_firstname || !formData.supplier_phone_number}
          >
            {!updateSupplierMutation.isLoading && <span className='indicator-label'>Update Supplier</span>}
            {updateSupplierMutation.isLoading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Please wait...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export { Updatesupplier };